import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatResultValue } from "../calculators/CalculatorRenderer";
import { getCalculatorByKey } from "../calculators/registry";
import { fetchScenarios, deleteScenario } from "../lib/api";

/**
 * "My Scenarios" (Task 10): every scenario saved from any calculator's "Save this scenario"
 * button, reviewable, re-runnable (re-opens the calculator pre-filled with the saved inputs),
 * or deletable.
 */
export function MyScenariosPage() {
  const [scenarios, setScenarios] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchScenarios()
      .then(setScenarios)
      .catch(() => toast.error("Couldn't load your saved scenarios."));
  }, []);

  async function handleDelete(id) {
    try {
      await deleteScenario(id);
      setScenarios((prev) => prev.filter((s) => s.id !== id));
    } catch {
      toast.error("Couldn't delete that scenario.");
    }
  }

  function handleRerun(scenario) {
    navigate(`/calculators/${scenario.calculator_key}`, { state: { prefillInputs: scenario.inputs } });
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="text-2xl font-semibold text-text">My Scenarios</h1>
      <p className="mt-1 text-sm text-muted">Calculator results you've saved for later.</p>

      {scenarios === null ? null : scenarios.length === 0 ? (
        <p className="mt-6 text-sm text-muted">
          Nothing saved yet — use "Save this scenario" on any calculator's results.
        </p>
      ) : (
        <div className="mt-6 flex flex-col gap-3">
          {scenarios.map((scenario) => {
            const config = getCalculatorByKey(scenario.calculator_key);
            const headline = scenario.outputs?.headline;
            return (
              <Card key={scenario.id}>
                <CardHeader className="flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">{scenario.label}</CardTitle>
                    <CardDescription>
                      {config?.title || scenario.calculator_key} · {new Date(scenario.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  {headline && (
                    <div className="text-right">
                      <p className="text-xs text-muted">{headline.label}</p>
                      <p className="text-lg font-semibold text-gold">{formatResultValue(headline)}</p>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="flex gap-2">
                  <Button size="sm" onClick={() => handleRerun(scenario)} disabled={!config}>
                    Re-run
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(scenario.id)}>
                    Delete
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
